import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle, RefreshCw, RotateCcw, XCircle, ServerCrash } from "lucide-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
const SECRET_STORAGE_KEY = "pa_admin_jobs_secret_v1";

interface JobRow {
  id: number;
  userId: string | null;
  status: string;
  originalFilename: string;
  mode: string | null;
  sourceType: string;
  rssEpisodeUrl: string | null;
  workerId: string | null;
  claimedAt: string | null;
  heartbeatAt: string | null;
  processingStartedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface ListResponse {
  jobs: JobRow[];
}


function adminFetch(secret: string, path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("x-admin-secret", secret);
  if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
  return fetch(`${basePath}/api${path}`, { ...init, headers });
}

function timeAgo(isoString: string | null): string {
  if (!isoString) return "—";
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  return `${diffHr}h ago`;
}

function statusColor(status: string): string {
  switch (status) {
    case "analyzing": return "bg-blue-500/15 text-blue-400 border-blue-500/30";
    case "abbreviating": return "bg-violet-500/15 text-violet-400 border-violet-500/30";
    case "queued": return "bg-amber-500/15 text-amber-400 border-amber-500/30";
    default: return "bg-muted text-muted-foreground border-border";
  }
}

export default function AdminJobsPage() {
  const [secret, setSecret] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.sessionStorage.getItem(SECRET_STORAGE_KEY) ?? "";
  });
  const [secretInput, setSecretInput] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyJobId, setBusyJobId] = useState<number | null>(null);
  const [actionResults, setActionResults] = useState<Map<number, { ok: boolean; message: string }>>(new Map());

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Job Manager · Admin · Podcasts Abbreviated";
    return () => {
      meta.remove();
      document.title = prevTitle;
    };
  }, []);

  const load = useCallback(async (theSecret: string) => {
    if (!theSecret) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(theSecret, "/admin/jobs");
      if (res.status === 401) {
        setError("That secret didn't work. Try again.");
        setData(null);
        window.sessionStorage.removeItem(SECRET_STORAGE_KEY);
        setSecret("");
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(typeof body?.error === "string" ? body.error : `Request failed (${res.status})`);
        return;
      }
      setData(await res.json() as ListResponse);
    } catch {
      setError("Couldn't reach the server.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (secret) void load(secret);
  }, [secret, load]);

  function handleSecretSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = secretInput.trim();
    if (!trimmed) return;
    window.sessionStorage.setItem(SECRET_STORAGE_KEY, trimmed);
    setSecret(trimmed);
    setSecretInput("");
  }

  async function resetJob(jobId: number, target: "queued" | "failed") {
    if (!secret || !operatorId.trim()) return;
    setBusyJobId(jobId);
    setActionResults((prev) => {
      const next = new Map(prev);
      next.delete(jobId);
      return next;
    });
    try {
      const res = await adminFetch(secret, `/admin/jobs/${jobId}/reset`, {
        method: "POST",
        body: JSON.stringify({ target, operatorId: operatorId.trim() }),
      });
      if (res.status === 401) {
        window.sessionStorage.removeItem(SECRET_STORAGE_KEY);
        setSecret("");
        setData(null);
        setActionResults(new Map());
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof body?.error === "string" ? body.error : `Action failed (${res.status})`;
        setActionResults((prev) => new Map(prev).set(jobId, { ok: false, message: msg }));
        return;
      }
      setActionResults((prev) => new Map(prev).set(jobId, {
        ok: true,
        message: `Job #${jobId} reset to "${target}" (was "${body.previousStatus}")`,
      }));
      await load(secret);
    } catch {
      setActionResults((prev) => new Map(prev).set(jobId, { ok: false, message: "Couldn't reach the server." }));
    } finally {
      setBusyJobId(null);
    }
  }

  function handleSignOut() {
    window.sessionStorage.removeItem(SECRET_STORAGE_KEY);
    setSecret("");
    setData(null);
    setActionResults(new Map());
  }

  if (!secret) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-border/60 bg-card/40">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-1">
              <ServerCrash size={20} className="text-amber-400" />
              <h1 className="text-xl font-semibold">Job Manager</h1>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Enter the <code className="font-mono text-xs">ADMIN_SECRET</code> to view and reset stuck jobs. The secret is held in this tab only.
            </p>
            <form onSubmit={handleSecretSubmit} className="space-y-4">
              <div>
                <Label htmlFor="admin-secret" className="font-mono uppercase text-[11px] tracking-wider text-muted-foreground mb-2 block">
                  Admin secret
                </Label>
                <Input
                  id="admin-secret"
                  type="password"
                  autoComplete="off"
                  value={secretInput}
                  onChange={(e) => setSecretInput(e.target.value)}
                  placeholder="••••••••••••"
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <Button type="submit" className="w-full font-mono uppercase tracking-wider">
                Continue
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const jobs = data?.jobs ?? [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <ServerCrash size={20} className="text-amber-400" />
              Job Manager
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              In-flight jobs · queued / analyzing / abbreviating
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void load(secret)}
              disabled={loading}
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <><RefreshCw size={13} className="mr-1.5" />Refresh</>}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              Forget secret
            </Button>
          </div>
        </div>

        <div className="mb-6">
          <Label htmlFor="operator-id" className="font-mono uppercase text-[11px] tracking-wider text-muted-foreground mb-1.5 block">
            Your name or email (required for all actions)
          </Label>
          <Input
            id="operator-id"
            type="text"
            value={operatorId}
            onChange={(e) => setOperatorId(e.target.value)}
            placeholder="e.g. alice@example.com"
            className="max-w-xs font-mono text-sm"
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3 mb-4">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading…
          </div>
        ) : jobs.length === 0 ? (
          <Card className="border-border/60 bg-card/40">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No in-flight jobs right now. All clear.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const result = actionResults.get(job.id);
              const isBusy = busyJobId === job.id;
              const canAct = !!operatorId.trim() && !isBusy;
              return (
                <Card key={job.id} className="border-border/60 bg-card/40">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-muted-foreground">#{job.id}</span>
                          <Badge
                            variant="outline"
                            className={`font-mono text-[10px] uppercase tracking-wider ${statusColor(job.status)}`}
                          >
                            {job.status}
                          </Badge>
                          {job.sourceType === "rss" && (
                            <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-wider bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                              RSS
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium mt-1 break-all">{job.originalFilename}</p>
                        {job.rssEpisodeUrl && (
                          <p className="text-xs text-muted-foreground font-mono break-all mt-0.5">{job.rssEpisodeUrl}</p>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono shrink-0 text-right space-y-0.5">
                        <p>Created {timeAgo(job.createdAt)}</p>
                        {job.processingStartedAt && <p>Started {timeAgo(job.processingStartedAt)}</p>}
                        {job.heartbeatAt && <p>Heartbeat {timeAgo(job.heartbeatAt)}</p>}
                      </div>
                    </div>

                    <dl className="grid sm:grid-cols-3 gap-x-6 gap-y-1 text-sm mb-4">
                      <div>
                        <dt className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">User</dt>
                        <dd className="font-mono text-xs break-all">{job.userId ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Mode</dt>
                        <dd className="font-mono text-xs">{job.mode ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Worker</dt>
                        <dd className="font-mono text-xs break-all">{job.workerId ?? "—"}</dd>
                      </div>
                      {job.errorMessage && (
                        <div className="sm:col-span-3">
                          <dt className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Error</dt>
                          <dd className="text-xs text-destructive break-all">{job.errorMessage}</dd>
                        </div>
                      )}
                    </dl>

                    {result && (
                      <div className={`flex items-start gap-2 text-xs rounded-md p-2.5 mb-3 ${
                        result.ok
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : "bg-destructive/10 text-destructive border border-destructive/30"
                      }`}>
                        {result.ok ? <RotateCcw size={13} className="shrink-0 mt-0.5" /> : <AlertCircle size={13} className="shrink-0 mt-0.5" />}
                        <span>{result.message}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canAct}
                        onClick={() => void resetJob(job.id, "queued")}
                        className="font-mono text-xs"
                        title={!operatorId.trim() ? "Enter your name or email above first" : undefined}
                      >
                        {isBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <RotateCcw size={12} className="mr-1" />}
                        Reset to queued
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!canAct}
                        onClick={() => void resetJob(job.id, "failed")}
                        className="font-mono text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                        title={!operatorId.trim() ? "Enter your name or email above first" : undefined}
                      >
                        {isBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <XCircle size={12} className="mr-1" />}
                        Mark as failed
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
